<template>
  <div class="jd-features">
    <!-- Salary range -->
    <div v-if="salaryLabel" class="jd-feature jd-feature--salary">
      <svg-salary class="jd-feature__icon svg-fill" />
      <p class="jd-feature__text">{{ salaryLabel }}</p>
    </div>
    <!-- Vacancies -->
    <div v-if="vacancyLabel" class="jd-feature jd-feature--vacancy">
      <svg-vacancy class="jd-feature__icon svg-stroke" />
      <p class="jd-feature__text">{{ vacancyLabel }}</p>
    </div>
    <!-- Working hour range -->
    <div v-if="workingHourLabel" class="jd-feature jd-feature--working-hour">
      <svg-time class="jd-feature__icon svg-fill" />
      <p>{{ workingHourLabel }}</p>
    </div>
    <!-- Working day range -->
    <div v-if="workingDayLabel" class="jd-feature jd-feature--working-day">
      <svg-calendar class="jd-feature__icon svg-fill" />
      <p class="jd-feature__text">{{ workingDayLabel }}</p>
    </div>
    <!-- Districts -->
    <div v-if="districtLinks.length" class="jd-feature jd-feature--districts">
      <svg-location class="jd-feature__icon svg-fill" />
      <p class="jd-feature__text">
        <template v-for="(districtLink, i) in districtLinks">
          <template v-if="i > 0">, </template>
          <n-link
            :key="`JobFeatureDistrict-${districtLink.title}`"
            class="jd-feature__district"
            :title="districtLink.title"
            :to="districtLink.path"
            >{{ districtLink.title }}</n-link
          >
        </template>
      </p>
    </div>
  </div>
</template>

<script>
import { getDistrictLocaleKey } from '../../../libs/mapping/district'
import { getParamSearchPath } from '../../../libs/mapping/searchParam'
import SvgCalendar from '../../../assets/svg/calendar.svg?inline'
import SvgLocation from '../../../assets/svg/location.svg?inline'
import SvgSalary from '../../../assets/svg/salary.svg?inline'
import SvgTime from '../../../assets/svg/time.svg?inline'
import SvgVacancy from '../../../assets/svg/vacancy.svg?inline'

export default {
  components: {
    'svg-calendar': SvgCalendar,
    'svg-location': SvgLocation,
    'svg-salary': SvgSalary,
    'svg-time': SvgTime,
    'svg-vacancy': SvgVacancy,
  },
  computed: {
    districtLinks() {
      return this.districts
        .map((district = {}) => ({
          path: this.localePathName(getParamSearchPath({ district: [district.shortCode] }, this.currentLocale.code)),
          title: this.$t(getDistrictLocaleKey(district)),
        }))
        .filter(({ title }) => title) // Filter out non existing districts
    },
    salaryLabel() {
      // Return either hourly or monthly salary
      if (this.hourlySalaryRange) {
        return `$${this.hourlySalaryRange}/${this.$t('general.hour')}`
      } else if (this.monthlySalaryRange) {
        return `$${this.monthlySalaryRange}k/${this.$t('general.month')}`
      }
      return ''
    },
    vacancyLabel() {
      if (this.vacancy >= 0) {
        return this.$tc('jobDetail.vacancy', this.vacancy)
      }
      return ''
    },
    workingDayLabel() {
      if (this.workingDayRange) {
        return this.$tc('jobDetail.daysPerWeek', this.workingDayRange, {
          days: this.workingDayRange,
        })
      }
      return ''
    },
    workingHourLabel() {
      if (this.workingHourRange) {
        return this.$tc('jobDetail.hoursPerDay', this.workingHourRange, {
          hours: this.workingHourRange,
        })
      }
      return ''
    },
  },
  props: {
    districts: {
      default: () => [],
      type: Array,
    },
    hourlySalaryRange: {
      default: '',
      type: String,
    },
    monthlySalaryRange: {
      default: '',
      type: String,
    },
    vacancy: {
      default: 0,
      type: Number,
    },
    workingDayRange: {
      default: '',
      type: String,
    },
    workingHourRange: {
      default: '',
      type: String,
    },
  },
}
</script>

<style lang="postcss" scoped>
.jd-features {
  @apply flex flex-wrap;
}
.jd-feature {
  @apply flex items-center w-1/2 mb-4 pr-3;

  &.jd-feature--districts {
    @apply w-full;
  }
}
.jd-feature__icon {
  @apply flex-shrink-0 mr-2 text-grey-400;
  width: 1.3125em;
  height: 1.3125em;
}
.jd-feature__district {
  @apply underline text-indigo;
}

@screen desktop {
  .jd-features {
    @apply mb-1;
  }
  .jd-feature {
    @apply w-1/3 mb-5 pr-4;
  }
  .jd-feature.jd-feature--districts {
    @apply w-2/3;
  }
  .jd-feature__icon {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>
